import React, { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';

import { screenNameRoutes } from '../../constants';
import { useRedirectToLastScreen } from '../../customHooks';

import { authUserOperations } from '@evdy/web-redux/dist/lib/authUser';

import styles from './StepNavigator.module.scss';

import chevronDown from '../../public/images/chevron-down-grey.svg';
import chevronUp from '../../public/images/chevron-up-grey.svg';

const { saveLastScreen } = authUserOperations;

const {
  'purchase-navigator': navigatorContainerClass,
  'mobile-chevron': mobileChevronClass,
  'mobile-current-step': currentStepMobileClass,
  'steps-container': stepsContainerClass,
  'dropdown-header': headerClass,
  'step-container': stepContainerClass,
  'step-name': stepNameDisplayClass,
  'step-complete': stepCompleteClass,
  'step-copy-container': stepCopyContainerClass,
  'active-step': activeStepClass,
  'complete-step-bg': completeStepBgClass,
  step: stepClass
} = styles;

const StepNavigator = () => {
  const dispatch = useDispatch();
  const { authUser } = useSelector(({ authUser }) => ({
    authUser: authUser?.data
  }));
  const {
    lastScreen: { screen } = {},
    packageName = '',
    funeralHome: { name: funeralHomeName } = {},
    userInfo: { phone } = {}
  } = authUser?.preneed ?? {};

  // mobile nav drop down state
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);

  const skippedCreate = screen === 'completeDetails' || screen === 'submitOrder';

  const purchaseSteps = [
    {
      stepDisplayName: 'select experience',
      route: screenNameRoutes['selectPackage'],
      stepCompleteData: packageName,
      canQuickSelect: true,
      isComplete: !!packageName
    },
    {
      stepDisplayName: 'choose funeral home',
      route: screenNameRoutes['chooseProvider'],
      stepCompleteData: funeralHomeName,
      canQuickSelect: !!packageName,
      isComplete: !!funeralHomeName
    },
    {
      stepDisplayName: 'create account',
      route: screenNameRoutes['getUserPhone'],
      stepCompleteData: phone && 'Complete',
      canQuickSelect: !!funeralHomeName,
      isComplete: !!phone || skippedCreate
    },
    {
      stepDisplayName: 'review plan',
      route: screenNameRoutes['completeDetails'],
      canQuickSelect: !!funeralHomeName,
      isComplete: !!funeralHomeName
    },
    {
      stepDisplayName: 'secure your plan',
      route: screenNameRoutes['submitOrder'],
      canQuickSelect: !!funeralHomeName
    }
  ];

  // filter out non step navigator pages
  const screenNames = Object.keys(screenNameRoutes).filter(name => {
    if (['getEstimates', 'packageComparison', 'extraAccountDetails'].includes(name)) return false;
    return true;
  });

  // current step copy
  const currentStepCopy = useMemo(() => {
    return purchaseSteps.find(({ route }) => route === screenNameRoutes[screen])?.stepDisplayName;
  }, [purchaseSteps, screen]);

  const renderStep = (
    { stepDisplayName, stepCompleteData, canQuickSelect, isComplete },
    idx,
    isMobileDropdownOpen
  ) => {
    const handleStepClick = () => {
      if (canQuickSelect) {
        dispatch(saveLastScreen({ screen: screenNames[idx] }));
      }
    };

    const isActiveStep = screen === screenNames[idx];

    return (
      <div
        className={classNames(stepContainerClass, { 'dropdown-open': isMobileDropdownOpen })}
        key={stepDisplayName}
        onClick={handleStepClick}
      >
        <div
          className={classNames(stepClass, {
            [activeStepClass]: isActiveStep,
            [completeStepBgClass]: isComplete,
            'dropdown-open': isMobileDropdownOpen
          })}
        >
          {idx + 1}
        </div>
        <div className={stepCopyContainerClass}>
          <p
            className={classNames(stepNameDisplayClass, {
              [activeStepClass]: isActiveStep,
              [completeStepBgClass]: isComplete,
              'dropdown-open': isMobileDropdownOpen
            })}
          >
            {stepDisplayName}
          </p>
          {!!stepCompleteData && (
            <p className={classNames(stepCompleteClass, { 'dropdown-open': isMobileDropdownOpen })}>
              {stepCompleteData}
            </p>
          )}
        </div>
      </div>
    );
  };

  //on mount determine last screen visited by user and redirect
  useRedirectToLastScreen({});

  return (
    <div className={classNames(navigatorContainerClass, { 'dropdown-open': isMobileDropdownOpen })}>
      <div className={classNames(stepsContainerClass, { 'dropdown-open': isMobileDropdownOpen })}>
        <div
          className={classNames(headerClass, { 'dropdown-closed': !isMobileDropdownOpen })}
          onClick={() => setIsMobileDropdownOpen(false)}
        >
          <p>Hide Progress</p>
          <img src={chevronUp} alt="chev-up" />
        </div>
        {purchaseSteps.map((step, idx) => renderStep(step, idx, isMobileDropdownOpen))}
        <img
          className={classNames(mobileChevronClass, { 'dropdown-open': isMobileDropdownOpen })}
          src={chevronDown}
          alt="chev-down"
          onClick={() => setIsMobileDropdownOpen(true)}
        />
      </div>
      <p className={classNames(currentStepMobileClass, { 'dropdown-open': isMobileDropdownOpen })}>
        {currentStepCopy}
      </p>
    </div>
  );
};

export default StepNavigator;
